import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { DEFAUTL_BASE64_START_CODE, DEFAUTL_QR_START_CODE } from '../../../variables';
import { Types } from '../../../state/actionTypes';
import { _dispatch as dispatch } from '../../../state/actions';
import PDF47CRReader from './PDF47CRReader';
import PDF47CRMinorReader, { isMinorCRDocument } from './PDF47CRMinorReader';
import CRLicenseReader, { CR_LICENSE_REGEX } from './CRLicenseReader';
import { parseMrz } from './parseMrz';
import { coerceInfraredData, isConsistentInfraredData } from './InfraredDataValidator';
import InfraredScanErrorDialog from './InfraredScanErrorDialog';

const ONLY_DIGITS_REGEX = /^\d+$/
export const SCAN_SUCCESS_GUARD_MS = 1500

const InfraredReader = (props) => {
  const {
    onChangDataByID,
    onChangeUniqueIdentificator,
    isCostaRica = false,
    loading,
    dispatch,
  } = props

  const [scanErrorOpen, setScanErrorOpen] = useState(false)
  const lastSuccessfulScanAt = useRef(0)

  const openScanError = useCallback(() => {
    setScanErrorOpen(true)
  }, [])

  const closeScanError = useCallback(() => {
    setScanErrorOpen(false)
  }, [])

  const hasRecentSuccessfulScan = useCallback(() => {
    return Date.now() - lastSuccessfulScanAt.current < SCAN_SUCCESS_GUARD_MS
  }, [])

  const rejectInconsistentScan = useCallback((data) => {
    if (hasRecentSuccessfulScan()) return
    if (data) console.warn('[InfraredReader] Datos inconsistentes, se descartan:', data)
    openScanError()
  }, [hasRecentSuccessfulScan, openScanError])

  const safeOnChangDataByID = useCallback((data, options = {}) => {
    const coerced = coerceInfraredData(data)
    if (isConsistentInfraredData(coerced, { isCostaRica, ...options })) {
      lastSuccessfulScanAt.current = Date.now()
      onChangDataByID(coerced)
      return
    }
    rejectInconsistentScan(data)
  }, [isCostaRica, onChangDataByID, rejectInconsistentScan])

  const processCRLicense = useCallback((code) => {
    console.log('processCRLicense', code)
    const data = CRLicenseReader(code)
    safeOnChangDataByID(data);
  }, [safeOnChangDataByID])

  const processOnlyNumber = useCallback((code) => {
    console.log('processOnlyNumber', code)
    const data = {
      identification: code,
      first_name: '',
      last_name: '',
      last_name_two: '',
    }
    safeOnChangDataByID(data);
  }, [safeOnChangDataByID])

  const processUnsupportedFormat = useCallback((code) => {
    console.log('processUnsupportedFormat', code)
    openScanError()
  }, [openScanError])

  const processInvitation = useCallback((code) => {
    if (hasRecentSuccessfulScan()) return
    lastSuccessfulScanAt.current = Date.now()
    console.log('processInvitation', code)
    onChangeUniqueIdentificator(code)
  }, [hasRecentSuccessfulScan, onChangeUniqueIdentificator])

  const procesarCRDocumento = useCallback((code) => {
    console.log('procesarCRDocumento', code)
    const base64String = code.substring(7);
    const rawString = atob(base64String);
    const dataFromId = Array.from(rawString, char => char.charCodeAt(0));
    if (isMinorCRDocument(dataFromId)) {
      safeOnChangDataByID(PDF47CRMinorReader(dataFromId))
      return
    }
    safeOnChangDataByID(PDF47CRReader(dataFromId))
  }, [safeOnChangDataByID])

  const parserCode = useCallback((code) => {
    if (loading) return
    if (typeof code !== 'string') {
      console.error("La entrada no es una cadena (string).");
      return '';
    }
    const trimmed = code.trim()

    // Los prefijos explícitos van antes que los heurísticos: la regex de licencia no está anclada
    // y puede coincidir por azar (p. ej. "CI3") dentro del base64 de una cédula.
    if (trimmed.startsWith(DEFAUTL_BASE64_START_CODE)) return procesarCRDocumento(trimmed);
    if (trimmed.startsWith(DEFAUTL_QR_START_CODE)) return processInvitation(trimmed);
    if (CR_LICENSE_REGEX.test(trimmed)) return processCRLicense(trimmed);
    const parsedMrz = parseMrz(trimmed)
    if (parsedMrz) {
      const options = parsedMrz.documentType ? { documentType: parsedMrz.documentType } : {}
      return safeOnChangDataByID(parsedMrz.data, options)
    }
    if (ONLY_DIGITS_REGEX.test(trimmed)) return processOnlyNumber(trimmed);
    return processUnsupportedFormat(trimmed);
  }, [loading, procesarCRDocumento, processInvitation, processCRLicense, processOnlyNumber, processUnsupportedFormat, safeOnChangDataByID])


  useEffect(() => {
    if (window.electronAPI) {
      const handleScannerData = (data) => {
        parserCode(data);
      };
      const handleScannerStatus = (status) => {
        console.log('Estado del escáner:', status);
        dispatch({ type: Types.SET_SCANNER_STATUS, payload: status });
      };

      window.electronAPI.onScannerData(handleScannerData);
      window.electronAPI.onScannerStatus(handleScannerStatus);
      return () => {
        window.electronAPI?.removeAllListeners?.();
      };

    } else {
      console.log('Corriendo en un navegador web estándar. El escáner infrarrojo no está disponible.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <InfraredScanErrorDialog
      open={scanErrorOpen}
      onClose={closeScanError}
    />
  );
};

const mapStateToProps = state => {
  return {
    isCostaRica: !!state.profile.enabled_costa_rica,
    loading: state.backdrop.loading,
  }
}

const mapDispatchToProps = {
  dispatch,
}

export default connect(mapStateToProps, mapDispatchToProps)(InfraredReader)
